import { useEffect, useState } from 'react';
import { apiFetch, v1Path } from '../../lib/api';
import { isStepDone, launchSteps, sortLaunchSteps, stepProgress, type LaunchStepRow } from '../../lib/launch';
import { progressPercent } from '../../lib/progress';
import { initiativePath } from '../../lib/initiatives';
import { loadStatesById } from '../../lib/initiative-streams';
import type { ListResponse } from '../../lib/work-types';

export type LaunchStep = {
  id: string;
  key: string;
  title: string;
  start_date: string | null;
  due_date: string | null;
  live_at: string | null;
  done: boolean;
  percent: number;
  to: string;
};

const PAGE = 100;

/**
 * Launch steps of many initiatives in one go, across every stream.
 * Keyed by initiative id, in launch order.
 */
export function useLaunchStepsByInitiative(initiativeIds: string[], enabled: boolean): Map<string, LaunchStep[]> {
  const [steps, setSteps] = useState<Map<string, LaunchStep[]>>(new Map());
  const key = [...initiativeIds].sort().join(',');

  useEffect(() => {
    if (!enabled || key === '') {
      setSteps(new Map());
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      const rows: LaunchStepRow[] = [];
      for (let offset = 0; ; offset += PAGE) {
        const page = await apiFetch<ListResponse<LaunchStepRow>>(
          `${v1Path('/work-items')}?initiative_ids=${key}&show_sub_items=false&limit=${PAGE}&offset=${offset}`,
        );
        rows.push(...page.items);
        if (page.items.length < PAGE) break;
      }
      const statesById = await loadStatesById(Array.from(new Set(rows.map((row) => row.project_id))));
      const next = new Map<string, LaunchStep[]>();
      for (const initiativeId of key.split(',')) {
        next.set(
          initiativeId,
          sortLaunchSteps(launchSteps(rows, initiativeId)).map((row) => {
            const state = statesById.get(row.state_id);
            return {
              id: row.id,
              key: row.key,
              title: row.title,
              start_date: row.start_date,
              due_date: row.due_date,
              live_at: row.live_at ?? null,
              done: isStepDone(state),
              percent: row.live_at ? 100 : progressPercent(stepProgress(row, state)),
              to: `${initiativePath(initiativeId)}?peek=${encodeURIComponent(row.key)}`,
            };
          }),
        );
      }
      if (!cancelled) setSteps(next);
    };
    load().catch(() => {
      if (!cancelled) setSteps(new Map());
    });
    const onChanged = () => void load().catch(() => undefined);
    window.addEventListener('bb:work-items-changed', onChanged);
    return () => {
      cancelled = true;
      window.removeEventListener('bb:work-items-changed', onChanged);
    };
  }, [enabled, key]);

  return steps;
}
